import React, { useEffect, useState } from 'react';
import AdminPage from '../../components/admin/AdminPage';
import AdminPaginator from '../../components/admin/AdminPaginator';
import LoadingCard from '../../components/LoadingCard';
import {
  Table,
  Row,
  Col,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import useKyc from './hooks/useKyc';
import { KycItem } from './models/kycModel';
import { KycStatus } from './constants/kycConstants';
import KycStatusBadge from './components/KycStatusBadge';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import {
  getLandlordByEmailEndpoint,
  getLandlordByNameEndpoint,
} from '../accounting/services/accountingService';
import useAuth from '../../hooks/useAuth';
import { fetchResponseAdapter } from '../../adapters/fetchAdapter';
import GridSearchBar from '../../components/searchBar/GridSearchBar';
import { getOptionKycStatus } from './adapters/kycAdapter';

const KycList = () => {
  const pageName = 'Lista de formularios KYC';
  const pageParentName = 'Cumplimiento';
  const { kycList, isLoading, errorAlert, loadKycs, paginator } = useKyc();
  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  // Paginated data

  if (isLoading) {
    return <LoadingCard name={pageName} parent_name={pageParentName} />;
  }

  const kycFilters = [
    {
      label: 'Estado de KYC',
      value: 'status',
      apiParamName: 'status',
      inputType: 'select',
      inputTypeValues: getOptionKycStatus(),
      isUnique: false,
      placeholder: 'Seleccione...',
    },
    {
      label: 'Nombre inversionista',
      value: 'landlord_name',
      apiParamName: 'landlord_id',
      inputType: 'search',
      isUnique: true,
      placeholder: 'Ingrese el nombre del inversionista',
      inputTypeChoices: async (searchTerm: string) => {
        if (!searchTerm) return [];

        const { status, data } = await callEndpoint(
          getLandlordByNameEndpoint(searchTerm, token),
        );

        if (status === 200) {
          const response = fetchResponseAdapter(data);
          return response.data.map((landlord: any) => ({
            value: landlord.landlord_id,
            label: (
              <>
                <span>{landlord.name}</span>
                <small className="text-gray mx-2">{landlord.phone}</small>
              </>
            ),
          }));
        } else {
          return [];
        }
      },
    },
    {
      label: 'Email inversionista',
      value: 'landlord_email',
      apiParamName: 'landlord_id',
      inputType: 'search',
      isUnique: true,
      placeholder: 'Ingrese el email del inversionista',
      inputTypeChoices: async (searchTerm: string) => {
        if (!searchTerm) return [];

        const { status, data } = await callEndpoint(
          getLandlordByEmailEndpoint(searchTerm, token),
        );

        if (status === 200) {
          const response = fetchResponseAdapter(data);
          return response.data.map((landlord: any) => ({
            value: landlord.landlord_id,
            label: (
              <>
                <span>{landlord.name}</span>
                <small className="text-gray mx-2">{landlord.phone}</small>
              </>
            ),
          }));
        } else {
          return [];
        }
      },
    },
  ];

  return (
    <AdminPage
      name={pageName}
      parentName={pageParentName}
      footer={
        <AdminPaginator
          page={paginator.page}
          totalPages={paginator.pageCount}
          onPageChange={loadKycs}
        />
      }
    >
      <Row className="mt-3">
        <Col md="12" className="mb-3">
          <GridSearchBar filtersList={kycFilters} />
        </Col>
        <Col md="12">
          {errorAlert && <div className="text-danger mb-3">{errorAlert}</div>}
          <Table className="align-items-center table-flush" responsive>
            <thead>
              <tr>
                <th>Última actualización</th>
                <th>Invitado</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Proyecto</th>
                <th>Unidad</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {kycList.length > 0 ? (
                kycList.map((kyc: KycItem) => (
                  <tr key={kyc.id}>
                    <td>{kyc.updated_at}</td>
                    <td>{kyc.guest?.name || ''}</td>
                    <td>{kyc.guest?.email || ''}</td>
                    <td>{kyc.guest?.phone || ''}</td>
                    <td>{kyc.project_name}</td>
                    <td>{kyc.unit_name}</td>
                    <td>{<KycStatusBadge status={kyc.status} />}</td>
                    <td>
                      <UncontrolledDropdown>
                        <DropdownToggle
                          className="btn-icon-only text-light"
                          href="#"
                          role="button"
                          size="sm"
                          color=""
                          onClick={(e) => e.preventDefault()}
                        >
                          <i className="fas fa-ellipsis-v" />
                        </DropdownToggle>
                        <DropdownMenu className="dropdown-menu-arrow" right>
                          <DropdownItem href={`/kyc/${kyc.id}/detail`}>
                            Ver formulario de cumplimiento
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center">
                    No hay registros de KYC.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    </AdminPage>
  );
};

export default KycList;
